import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  PORTAL_I18N_NAMESPACES,
  type PortalLocale,
  SUPPORTED_LOCALES,
} from './constants';

import koCommon from '../locales/ko/common.json';
import koAuth from '../locales/ko/auth.json';
import koAdminNav from '../locales/ko/adminNav.json';
import koLayout from '../locales/ko/layout.json';
import koServices from '../locales/ko/services.json';
import koAdmin from '../locales/ko/admin.json';

import enCommon from '../locales/en/common.json';
import enAuth from '../locales/en/auth.json';
import enAdminNav from '../locales/en/adminNav.json';
import enLayout from '../locales/en/layout.json';
import enServices from '../locales/en/services.json';
import enAdmin from '../locales/en/admin.json';

function readStoredLocale(): PortalLocale | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (raw && (SUPPORTED_LOCALES as readonly string[]).includes(raw)) {
      return raw as PortalLocale;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function readQueryLocale(): PortalLocale | null {
  if (typeof window === 'undefined') return null;
  try {
    const param = new URLSearchParams(window.location.search).get('lang');
    if (param && (SUPPORTED_LOCALES as readonly string[]).includes(param)) {
      return param as PortalLocale;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function persistLocale(locale: PortalLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
}

export function resolveInitialLocale(): PortalLocale {
  return readQueryLocale() ?? readStoredLocale() ?? DEFAULT_LOCALE;
}

let initPromise: Promise<typeof i18n> | null = null;

export function initPortalI18n(): Promise<typeof i18n> {
  if (initPromise) return initPromise;

  const lng = resolveInitialLocale();
  if (lng !== DEFAULT_LOCALE) {
    persistLocale(lng);
  }

  initPromise = i18n
    .use(initReactI18next)
    .init({
      lng,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      ns: [...PORTAL_I18N_NAMESPACES],
      defaultNS: 'common',
      resources: {
        ko: {
          common: koCommon,
          auth: koAuth,
          adminNav: koAdminNav,
          layout: koLayout,
          services: koServices,
          admin: koAdmin,
        },
        en: {
          common: enCommon,
          auth: enAuth,
          adminNav: enAdminNav,
          layout: enLayout,
          services: enServices,
          admin: enAdmin,
        },
      },
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      returnEmptyString: false,
    })
    .then(() => {
      if (typeof document !== 'undefined') {
        document.documentElement.lang = i18n.language;
      }
      return i18n;
    });

  return initPromise;
}

export { i18n };
